import { FormBuilder, FormGroup, NonNullableFormBuilder } from '@angular/forms';
import { customOdfValidators } from '@app/shared/base-form/custom-odf-validator-config';
import {
    DescripteursFormation,
    DescripteursGroupement,
    DescripteursObjetFormation,
    DescripteursObjetMaquette,
    TypeObjetMaquette
} from '@pcscol/odf-api-v1-angular';
import { AbstractDescripteursForm } from '../../../../../../shared/form-models/abstract-descripteurs-form';

export class DescripteursObjetMaquetteForm extends AbstractDescripteursForm {
    readonly champsSpecifiques = {
        [TypeObjetMaquette.ObjetFormation]: ['type', 'nature', 'capaciteAccueil'],
        [TypeObjetMaquette.Formation]: ['typeFormation'],
        [TypeObjetMaquette.Groupement]: ['plageDeChoix', 'plageDeChoixMin', 'plageDeChoixMax', 'natureGroupement']
    };

    constructor(protected formGroup: FormGroup, protected typeObjetMaquette: TypeObjetMaquette) {
        super(formGroup, typeObjetMaquette);
        this.updateSpecificFormFields();
    }

    public static createForm(
        fb: FormBuilder | NonNullableFormBuilder,
        typeObjetMaquette: TypeObjetMaquette,
        descripteursObjetMaquette?: DescripteursObjetMaquette
    ) {
        const formGroup = fb.group(
            {
                // descripteurs de base
                libelle: fb.control<string | null>(null, { validators: customOdfValidators.descripteursObjetMaquette.libelle }),
                libelleLong: fb.control<string | null>(null, { validators: customOdfValidators.descripteursObjetMaquette.libelleLong }),
                // descripteurs OF
                description: fb.control<string | null>(null, { validators: customOdfValidators.descripteursObjetMaquette.description }),
                ects: fb.control<number | undefined>(undefined, { validators: customOdfValidators.descripteursObjetMaquette.ects }),
                structurePrincipale: fb.control<string | null>(null, {
                    validators: customOdfValidators.descripteursObjetMaquette.structurePrincipale
                }),
                stage: fb.control(false, { validators: customOdfValidators.descripteursObjetMaquette.stage }),
                nature: fb.control<string | null>(null, { validators: customOdfValidators.descripteursObjetMaquette.nature }),
                teleEnseignement: fb.control<boolean>(false, {
                    validators: customOdfValidators.descripteursObjetMaquette.teleEnseignement
                }),
                type: fb.control<string | null>(null, { validators: customOdfValidators.descripteursObjetMaquette.typeObjetFormation }),
                capaciteAccueil: fb.control<number | null>(null, {
                    validators: customOdfValidators.descripteursObjetMaquette.capaciteAccueil
                }),
                // FORMATION
                typeFormation: fb.control<string | null>(null, { validators: customOdfValidators.descripteursObjetMaquette.typeFormation }),
                // GROUPEMENT
                plageDeChoix: fb.control<boolean>(false),
                natureGroupement: fb.control<string | null>(null),
                plageDeChoixMin: fb.control(0, { validators: customOdfValidators.descripteursObjetMaquette.plageDeChoixMin }),
                plageDeChoixMax: fb.control(1, { validators: customOdfValidators.descripteursObjetMaquette.plageDeChoixMax })
            },
            {
                validators: [
                    control => {
                        if (control.value.plageDeChoixMin > (control.value.plageDeChoixMax ?? null)) {
                            // LOL javascript : 1 > null => true, 1 > undefined => false
                            return { plageDeChoixMinMax: true };
                        } else {
                            return null;
                        }
                    }
                ]
            }
        );
        if (descripteursObjetMaquette) {
            formGroup.patchValue({
                ...descripteursObjetMaquette,
                typeFormation: (descripteursObjetMaquette as DescripteursFormation)?.type?.code,
                type: (descripteursObjetMaquette as DescripteursObjetFormation)?.type?.code,
                nature: (descripteursObjetMaquette as DescripteursObjetFormation)?.nature?.code,
                natureGroupement: (descripteursObjetMaquette as DescripteursGroupement)?.natureGroupement?.code,

                // GPT
                plageDeChoix: !!(descripteursObjetMaquette as DescripteursGroupement).plageDeChoix,
                plageDeChoixMin: (descripteursObjetMaquette as DescripteursGroupement).plageDeChoix?.min ?? 0,
                plageDeChoixMax: (descripteursObjetMaquette as DescripteursGroupement).plageDeChoix?.max ?? 1
            });
        }
        return new DescripteursObjetMaquetteForm(formGroup, typeObjetMaquette);
    }

    public get asDescripteursObjetMaquette(): DescripteursObjetMaquette {
        switch (this.typeObjetMaquette) {
            case TypeObjetMaquette.Formation:
                return this.asDescripteursFormation;
            case TypeObjetMaquette.ObjetFormation:
                return this.asDescripteursObjetFormation;
            case TypeObjetMaquette.Groupement:
                return this.asDescripteursGroupement;
            default:
                throw new Error(
                    'DescripteursObjetMaquetteComponent#getDescripteursObjetMaquette: le type fourni en paramètre est incorrect'
                );
        }
    }

    private get asDescripteursFormation(): Required<DescripteursFormation> {
        const base: Required<DescripteursFormation> = {
            typeObjetMaquette: TypeObjetMaquette.Formation,
            libelle: this.formGroup.value.libelle,
            libelleLong: this.formGroup.value.libelleLong,
            ects: this.formGroup.value.ects,
            structurePrincipale: this.formGroup.value.structurePrincipale,
            teleEnseignement: this.formGroup.value.teleEnseignement,
            type: this.formGroup.value.typeFormation
        } as Required<DescripteursFormation>; // Cast parce que l'api est codée avec les pieds
        return base;
    }

    private get asDescripteursGroupement(): DescripteursGroupement {
        const plageDeChoix = this.formGroup.controls.plageDeChoix.value
            ? { min: this.formGroup.controls.plageDeChoixMin.value, max: this.formGroup.controls.plageDeChoixMax.value }
            : undefined;
        const base: DescripteursGroupement = {
            typeObjetMaquette: TypeObjetMaquette.Groupement,
            libelle: this.formGroup.value.libelle,
            libelleLong: this.formGroup.value.libelleLong,
            plageDeChoix,
            natureGroupement: this.formGroup.value.natureGroupement || null
        } as DescripteursGroupement; // Cast parce que l'api est codée avec les pieds
        return base;
    }

    private get asDescripteursObjetFormation(): DescripteursObjetFormation {
        const base: DescripteursObjetFormation = {
            typeObjetMaquette: TypeObjetMaquette.ObjetFormation,
            libelle: this.formGroup.value.libelle,
            libelleLong: this.formGroup.value.libelleLong,
            ects: this.formGroup.value.ects,
            structurePrincipale: this.formGroup.value.structurePrincipale,
            stage: this.formGroup.value.stage,
            teleEnseignement: this.formGroup.value.teleEnseignement,
            nature: this.formGroup.value.nature || null,
            type: this.formGroup.value.type,
            capaciteAccueil: this.formGroup.value.capaciteAccueil
        } as DescripteursObjetFormation; // Cast parce que l'api est codée avec les pieds
        return base;
    }

    updateTypeObjetMaquette(typeObjetMaquette: TypeObjetMaquette) {
        this.typeObjetMaquette = typeObjetMaquette;
        this.updateSpecificFormFields();
    }

    togglePLageDeChoix() {
        this.formGroup.controls.plageDeChoix.patchValue(!this.formGroup.controls.plageDeChoix.value);
        this.formGroup.controls.plageDeChoix.markAsDirty();
    }

    toggleTeleenseignement() {
        this.formGroup.controls.teleEnseignement.patchValue(!this.formGroup.controls.teleEnseignement.value);
        this.formGroup.controls.teleEnseignement.markAsDirty();
    }

    private updateSpecificFormFields() {
        Object.keys(this.champsSpecifiques).forEach((typeObjetName: string) => {
            if (this.typeObjetMaquette === typeObjetName) {
                this.champsSpecifiques[typeObjetName].forEach((fieldName: string) => {
                    this.formGroup.controls[fieldName].enable();
                });
            } else {
                this.champsSpecifiques[typeObjetName].forEach((fieldName: string) => {
                    this.formGroup.controls[fieldName].reset();
                    this.formGroup.controls[fieldName].disable();
                });
            }
        });
    }

    public disable() {
        this.formGroup.disable();
    }
}
